// pages/Auth/UpdatePassword.tsx
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import AuthService from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardHeader, CardContent } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { toast } from "sonner";
import { Loader2, Lock, Eye, EyeOff, CheckCircle } from "lucide-react";
import { Progress } from "@/components/ui/progress";

const updatePasswordSchema = z
  .object({
    oldPassword: z.string().min(1, "Current password is required"),
    password: z
      .string()
      .min(8, "At least 8 characters")
      .regex(/[A-Z]/, "One uppercase letter")
      .regex(/[a-z]/, "One lowercase letter")
      .regex(/[0-9]/, "One number")
      .regex(/[^A-Za-z0-9]/, "One special character"),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  });

type UpdatePasswordFormData = z.infer<typeof updatePasswordSchema>;

export default function UpdatePassword() {
  const [isLoading, setIsLoading] = useState(false);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();

  const form = useForm<UpdatePasswordFormData>({
    resolver: zodResolver(updatePasswordSchema),
    defaultValues: {
      oldPassword: "",
      password: "",
      confirmPassword: "",
    },
  });

  const password = form.watch("password");

  // Calculate password strength
  const calculateStrength = (pwd: string) => {
    if (!pwd) return 0;
    let strength = 0;
    if (pwd.length >= 8) strength += 20;
    if (pwd.length >= 12) strength += 20;
    if (/[A-Z]/.test(pwd)) strength += 20;
    if (/[a-z]/.test(pwd)) strength += 20;
    if (/[0-9]/.test(pwd)) strength += 10;
    if (/[^A-Za-z0-9]/.test(pwd)) strength += 10;
    return strength;
  };

  const passwordStrength = calculateStrength(password);

  const onSubmit = async (data: UpdatePasswordFormData) => {
    setIsLoading(true);
    try {
      console.log("🔐 Updating password...");
      const { error } = await AuthService.updatePassword(data.password, data.oldPassword);

      if (error) {
        console.error("❌ Password update error:", error);
        if (error.message?.includes("Incorrect username or password") || error.message?.includes("NotAuthorizedException")) {
          toast.error("Incorrect current password");
        } else if (error.message?.includes("LimitExceeded")) {
          toast.error("Too many attempts. Please try again later.");
        } else {
          toast.error(error.message || "Failed to update password");
        }
        return;
      }

      console.log("✅ Password updated successfully");
      toast.success("Password updated successfully!");

      // Navigate back to profile or dashboard
      navigate("/profile");

    } catch (error: any) {
      console.error("❌ Unexpected error:", error);
      toast.error("An unexpected error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f7f8fa] font-sans px-4">
      <Card className="w-full max-w-md p-6 shadow-lg">
        <CardHeader className="text-center space-y-2">
          <div className="mx-auto w-12 h-12 bg-[#1a7a4a]/10 rounded-full flex items-center justify-center mb-4">
            <Lock className="h-6 w-6 text-[#1a7a4a]" />
          </div>
          <h1 className="text-3xl font-extrabold text-[#1a7a4a] tracking-tight">Change Password</h1>
          <p className="text-muted-foreground">
            Update your account password
          </p>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">

              <FormField
                control={form.control}
                name="oldPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Current Password</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          placeholder="Enter current password"
                          type={showOldPassword ? "text" : "password"}
                          autoComplete="current-password"
                          {...field}
                          disabled={isLoading}
                        />
                        <button
                          type="button"
                          onClick={() => setShowOldPassword(!showOldPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showOldPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>New Password</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          placeholder="Enter new password"
                          type={showPassword ? "text" : "password"}
                          autoComplete="new-password"
                          {...field}
                          disabled={isLoading}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </FormControl>
                    <div className="space-y-2 mt-2">
                      <div className="flex items-center justify-between text-xs">
                        <span>Password strength</span>
                        <span
                          className={
                            passwordStrength < 40
                              ? "text-red-500"
                              : passwordStrength < 70
                                ? "text-yellow-500"
                                : "text-green-500"
                          }
                        >
                          {passwordStrength < 40
                            ? "Weak"
                            : passwordStrength < 70
                              ? "Medium"
                              : "Strong"}
                        </span>
                      </div>
                      <Progress value={passwordStrength} className="h-1" />
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Confirm New Password</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          placeholder="Confirm new password"
                          type={showConfirmPassword ? "text" : "password"}
                          autoComplete="new-password"
                          {...field}
                          disabled={isLoading}
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setShowConfirmPassword(!showConfirmPassword)
                          }
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showConfirmPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="bg-muted p-3 rounded-lg text-xs space-y-1">
                <p className="font-semibold mb-1">Password must contain:</p>
                <div className="space-y-1">
                  {[
                    {
                      test: password.length >= 8,
                      text: "At least 8 characters",
                    },
                    {
                      test: /[A-Z]/.test(password),
                      text: "One uppercase letter",
                    },
                    {
                      test: /[a-z]/.test(password),
                      text: "One lowercase letter",
                    },
                    { test: /[0-9]/.test(password), text: "One number" },
                    {
                      test: /[^A-Za-z0-9]/.test(password),
                      text: "One special character",
                    },
                  ].map((rule, index) => (
                    <div key={index} className="flex items-center gap-2">
                      {rule.test ? (
                        <CheckCircle className="h-3 w-3 text-green-500" />
                      ) : (
                        <div className="h-3 w-3 rounded-full border border-muted-foreground" />
                      )}
                      <span
                        className={
                          rule.test ? "text-green-700" : "text-muted-foreground"
                        }
                      >
                        {rule.text}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-4">
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={() => navigate("/profile")}
                  disabled={isLoading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="w-full bg-[#1a7a4a] hover:bg-[#15633c] text-white rounded-2xl h-12 shadow-lg shadow-green-100 transition-all hover:scale-[1.02] font-semibold"
                  disabled={isLoading || passwordStrength < 60}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    "Update Password"
                  )}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
